/**
 * Landscape driving cab pass (ISO ID-1), Chiltern-style front; conditions + digital on reverse.
 * Click / Enter flips the card in 3D.
 */
import React, { useCallback, useId, useState } from "react";
import { translateUi } from './i18n/index';
import { QRCodeSVG } from "qrcode.react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { cabPassIsExpired, formatUkOrdinalLongDate, passSubtitleForColor, routesDisplayText } from "./drivingCabPassFormat";

/** Single overlay on the card frame (does not rotate with flip) so front/back never show duplicate stamps. */
function CabPassExpiredOverlay() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[80] rounded-[0.8mm] bg-black/38 [print-color-adjust:exact]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[81] overflow-hidden rounded-[0.8mm] [print-color-adjust:exact]"
      >
        <span
          className="absolute left-[50%] top-[50%] origin-center -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-[11mm] font-black uppercase leading-none tracking-[0.2em] text-red-700/95 -rotate-[27deg] [text-shadow:0_0_2px_rgb(255_255_255),0_0_10px_rgb(255_255_255),0_0_18px_rgb(254_226_226)] max-[640px]:text-[12.5mm]"
        > {translateUi("EXPIRED")} </span>
      </div>
    </>
  );
}

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** ISO ID-1 landscape (long edge horizontal). */
export const CAB_PASS_CARD_WIDTH_MM = "85.6mm";
export const CAB_PASS_CARD_HEIGHT_MM = "53.98mm";

/** Slight separation between flip faces (+ WebKit prefixes) avoids photo/QR/signature flashing through mid-rotate. */
const FLIP_FACE_Z = "0.0625rem";

const CARD_BG: Record<string, string> = {
  green: "#15803d",
  red: "#b91c1c",
  yellow: "#ca8a04",
  blue: "#0369a1",
  black: "#1e293b",
};

const LABEL_GREEN: Record<string, string> = {
  green: "#166534",
  red: "#991b1b",
  yellow: "#854d0e",
  blue: "#0c4a6e",
  black: "#0f172a",
};

export type DrivingCabPassCardProps = {
  passColor: string;
  holderName: string;
  holderTitle: string;
  holderDept: string;
  employeeId: string;
  passNumber: string;
  validFrom: string;
  validTo: string;
  validityScope?: string;
  issuedByName: string;
  issuedByDept: string;
  endorsements: string;
  issuerSignatureUrl?: string;
  dateRangeOk: boolean;
  companyName?: string;
  companyAddress?: string;
  /** Company logo for the top-right box; when empty the box stays blank (no company name fallback). */
  companyLogoUrl?: string;
  forcePaperStyle?: boolean;
  /** Headshot on front; usually employee profile image. */
  holderPhotoUrl?: string;
  /** When set, QR is shown on the front in a lower cell matching the photo strip (same column width). */
  verifyUrl?: string;
  /** When false, front only (no flip). */
  interactive?: boolean;
  /** Fixed ISO ID-1 mm layout (preview/print) — no viewport-based mobile enlargement. */
  id1Layout?: boolean;
};

function FrontField({
  label,
  children,
  labelClass,
  dense,
  labelWidthClass,
  id1Layout = false,
}: {
  label: string;
  children: React.ReactNode;
  labelClass: string;
  /** Tighter type when the column must fit many lines in ID-1 height. */
  dense?: boolean;
  /** Optional label width override for specific rows. */
  labelWidthClass?: string;
  id1Layout?: boolean;
}) {
  const mob = (desktop: string, mobile: string) => (id1Layout ? desktop : cn(desktop, mobile));
  return (
    <div
      className={cn(
        "flex min-w-0 gap-[0.65mm] leading-tight",
        dense
          ? mob("text-[2.05mm] leading-[1.12]", "max-[640px]:text-[3.5mm] max-[640px]:leading-tight")
          : "text-[2.05mm]"
      )}
    >
      <span
        className={cn(
          mob("shrink-0 w-[19.2mm] font-bold uppercase", "max-[640px]:w-[27mm]"),
          labelWidthClass,
          labelClass
        )}
      >
        {label}
      </span>
      <span className="min-w-0 whitespace-pre-wrap break-words font-normal text-black">{children}</span>
    </div>
  );
}

function ConditionsBlock({
  companyName,
  companyAddress,
}: {
  companyName?: string;
  companyAddress?: string;
}) {
  const name = String(companyName ?? "").trim();
  const address = String(companyAddress ?? "").trim();
  const co = name && address ? `${name}, ${address}` : name || address || "the issuing organisation";
  return (
    <div className="flex h-full min-h-0 flex-col text-black">
      <p className="shrink-0 text-center text-[2.35mm] font-bold uppercase tracking-wide text-[#475569]">{translateUi("Conditions of use")}</p>
      <ul className="mt-[0.9mm] min-h-0 flex-1 list-disc space-y-[0.6mm] pl-[3.35mm] text-[1.85mm] leading-snug">
        <li>
          The number of staff in the leading cab must not exceed limits set by company policy, unless specially authorised by a GREEN CAB PASS
          holder.
        </li>
        <li>{translateUi("This pass must be shown to the driver before entering the cab and at any other time as required.")}</li>
        <li>
          This pass must only be used by the person to whom it was issued. The holder must not interfere with any part of
          the cab, nor obstruct or distract the driver or guard.
        </li>
        <li>
          The holder may use it only when necessary for the proper performance of their duties. It remains the property of
          the issuing organisation and may be withdrawn without notice.
        </li>
      </ul>
      <p className="mt-[0.8mm] shrink-0 text-[1.65mm] leading-tight text-[#64748b]">
        If found please return to: {co}.
      </p>
    </div>
  );
}

export function DrivingCabPassCard({
  passColor,
  holderName,
  holderTitle,
  holderDept,
  employeeId,
  passNumber,
  validFrom,
  validTo,
  validityScope,
  issuedByName,
  issuedByDept,
  endorsements,
  issuerSignatureUrl,
  dateRangeOk,
  companyName,
  companyAddress,
  companyLogoUrl,
  holderPhotoUrl,
  verifyUrl,
  interactive = true,
  forcePaperStyle: _forcePaperStyle,
  id1Layout = false,
}: DrivingCabPassCardProps) {
  const c = String(passColor || "green").toLowerCase();
  const bg = CARD_BG[c] ?? CARD_BG.green;
  const labelTone = LABEL_GREEN[c] ?? LABEL_GREEN.green;
  const headingTone = c === "yellow" ? "text-black" : "text-white";
  const sub = passSubtitleForColor(c);
  const routesText = routesDisplayText(validityScope);
  const validFromDisplay = formatUkOrdinalLongDate(validFrom);
  const validToDisplay = formatUkOrdinalLongDate(validTo);
  const passExpired = cabPassIsExpired(validTo);
  const safeHolderName = String(holderName ?? "");
  const safeHolderTitle = String(holderTitle ?? "");
  const safeEmployeeId = String(employeeId ?? "");
  const safePassNumber = String(passNumber ?? "");
  const safeIssuedByName = String(issuedByName ?? "");
  const deptOnPass = String(issuedByDept ?? "").trim() || String(holderDept ?? "").trim();
  const safeCompanyLogoUrl = String(companyLogoUrl ?? "").trim();
  const safeEndorsements = String(endorsements ?? "");
  const end = safeEndorsements.trim() ? safeEndorsements.trim() : translateUi("NONE");
  const uid = useId().replace(/:/g, "");
  const [flipped, setFlipped] = useState(false);
  const mob = (desktop: string, mobile: string) => (id1Layout ? desktop : cn(desktop, mobile));

  const toggle = useCallback(() => {
    if (!interactive) return;
    setFlipped((v) => !v);
  }, [interactive]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!interactive) return;
      if (e.key !== "Enter" && e.key !== " ") return;
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("input, textarea, select, [contenteditable='true']")) return;
      e.preventDefault();
      toggle();
    },
    [interactive, toggle]
  );

  const front = (
    <div
      className="cab-pass-force-light cab-pass-flip-face cab-pass-flip-face-front flex h-full w-full flex-col overflow-x-hidden overflow-y-visible rounded-[0.8mm] border-[0.35mm] border-black/25 text-black shadow-sm [color-scheme:light]"
      style={{ backgroundColor: bg }}
    >
      <div className="flex shrink-0 items-start justify-between gap-[2mm] px-[2.2mm] pb-[1mm] pt-[1.6mm]">
        <div className="min-w-0">
          <p className={cn(mob("text-[3.55mm] font-black uppercase leading-[1.05] tracking-tight", "max-[640px]:text-[4.9mm]"), headingTone)}>
            {translateUi("DRIVING CAB PASS")}
          </p>
          <p className={cn(mob("mt-[0.4mm] text-[2.5mm] font-bold uppercase leading-tight", "max-[640px]:text-[3.35mm]"), headingTone)}>
            {sub}
          </p>
        </div>
        <div className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden border-[0.25mm] border-black bg-[#ffffff] p-[0.5mm]",
          mob("h-[11mm] w-[20mm]", "max-[640px]:h-[13.8mm] max-[640px]:w-[30mm]")
        )}>
          {safeCompanyLogoUrl ? (
            <img
              key={safeCompanyLogoUrl}
              src={safeCompanyLogoUrl}
              alt=""
              className="h-full w-full object-contain object-center [-webkit-transform:translateZ(0)] [transform:translateZ(0)]"
              decoding="sync"
              loading="eager"
            />
          ) : null}
        </div>
      </div>

      <div className={cn(
        "mx-[1.6mm] mb-[1.6mm] grid min-h-0 flex-1 grid-rows-1 overflow-hidden border-[0.2mm] border-black/30 bg-[#ffffff]",
        id1Layout
          ? "grid-cols-[minmax(0,1fr)_22mm]"
          : "grid-cols-[minmax(0,1fr)_max(26mm,34%)] sm:grid-cols-[minmax(0,1fr)_22mm]"
      )}>
        <div className={cn(
          "flex min-h-0 min-w-0 flex-col border-r-[0.2mm] border-black/20 px-[1.4mm] py-[0.9mm] text-black",
          id1Layout ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden print:overflow-visible [-webkit-overflow-scrolling:touch]"
        )}>
          <div className={cn(
            "flex shrink-0 flex-col gap-[0.38mm]",
            mob("", "max-[640px]:gap-[1.9mm] max-[640px]:pt-[0.9mm]")
          )}>
            <FrontField label="NAME:" labelClass={labelTone} dense id1Layout={id1Layout}>
              {safeHolderName.trim() || "—"}
            </FrontField>
            <FrontField label="TITLE:" labelClass={labelTone} dense id1Layout={id1Layout}>
              {safeHolderTitle.trim() || "—"}
            </FrontField>
            <FrontField label="DEPT:" labelClass={labelTone} dense id1Layout={id1Layout}>
              {deptOnPass || "—"}
            </FrontField>
            <FrontField label="ROUTES:" labelClass={labelTone} dense id1Layout={id1Layout}>
              {routesText}
            </FrontField>
            <FrontField label="PASS NO:" labelClass={labelTone} dense id1Layout={id1Layout}>
              {safePassNumber.trim() || "—"}
            </FrontField>
            <div className={cn(
              "flex min-w-0 gap-[0.65mm] text-[2.05mm] leading-[1.12]",
              mob("", "max-[640px]:text-[3.5mm] max-[640px]:leading-tight")
            )}>
              <span className={cn(mob("shrink-0 w-[19.2mm] font-bold uppercase", "max-[640px]:w-[27mm]"), labelTone)}>{translateUi("VALID:")}</span>
              <div className="min-w-0 font-normal text-black">
                <div className={mob("", "max-[640px]:hidden")}>
                  <span className={cn("font-bold", labelTone)}> {translateUi("From")} </span>
                  {validFromDisplay}
                </div>
                <div className={cn("mt-[0.1mm]", mob("", "max-[640px]:hidden"))}>
                  <span className={cn("font-bold", labelTone)}> To </span>
                  {validToDisplay}
                </div>
                <div className={cn(mob("hidden", "hidden whitespace-nowrap max-[640px]:block"))}>
                  <span className={cn("font-bold", labelTone)}> {translateUi("From")} </span>
                  {validFromDisplay}
                  <span className={cn("ml-[0.9mm] font-bold", labelTone)}> To </span>
                  {validToDisplay}
                </div>
              </div>
            </div>
          </div>
          <div className="min-h-0 flex-1" aria-hidden="true" />
          <div className={cn(
            "flex min-w-0 shrink-0 items-end gap-[0.65mm] border-b-[0.5mm] border-emerald-800 pb-[0.25mm]",
            mob("mt-[0.35mm]", "max-[640px]:mt-[0.2mm]")
          )}>
            <span className={cn(
              "shrink-0 text-[2.05mm] font-black uppercase leading-[1.12] text-[#047857]",
              mob("w-[23mm]", "max-[640px]:w-[31mm] max-[640px]:text-[3.5mm] max-[640px]:leading-tight")
            )}>
              {translateUi("SIGNATURE:")}
            </span>
            <div className="cab-pass-signature-slot flex h-[7.4mm] min-w-0 flex-1 items-end overflow-hidden [-webkit-transform:translateZ(0)] [transform:translateZ(0)]">
              {issuerSignatureUrl?.trim() ? (
                <img
                  key={issuerSignatureUrl.trim()}
                  src={issuerSignatureUrl.trim()}
                  alt={translateUi("Issuer signature")}
                  className="h-full w-auto max-w-full object-contain object-left-bottom [-webkit-transform:translateZ(0)] [transform:translateZ(0)]"
                  decoding="sync"
                  loading="eager"
                  onLoad={(ev) => {
                    const img = ev.currentTarget;
                    queueMicrotask(() => {
                      void img.offsetHeight;
                      window.dispatchEvent(new Event("resize"));
                      requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
                    });
                  }}
                />
              ) : (
                <span className="text-[1.8mm] font-semibold uppercase tracking-wide text-[#94a3b8]">{translateUi("Not signed")}</span>
              )}
            </div>
          </div>
          <div className={cn(
            "mt-[0.25mm] flex shrink-0 flex-col gap-[0.38mm] pt-[0.2mm]",
            mob("", "max-[640px]:gap-[1.9mm] max-[640px]:pt-[0.5mm]")
          )}>
            <FrontField
              label="ISSUED BY:"
              labelClass="text-[#047857] font-black uppercase"
              labelWidthClass={mob("w-[23mm]", "max-[640px]:w-[31mm]")}
              dense
              id1Layout={id1Layout}
            >
              {safeIssuedByName.trim() || "—"}
            </FrontField>
            <FrontField label="DEPT:" labelClass={labelTone} labelWidthClass={mob("w-[23mm]", "max-[640px]:w-[31mm]")} dense id1Layout={id1Layout}>
              {deptOnPass || "—"}
            </FrontField>
            <FrontField
              label="ENDORSEMENTS:"
              labelClass={labelTone}
              labelWidthClass={mob("w-[23mm]", "max-[640px]:w-[31mm]")}
              dense
              id1Layout={id1Layout}
            >
              {end}
            </FrontField>
          </div>
        </div>
        {verifyUrl?.trim() ? (
          <div className={cn(
            "flex min-h-0 w-full min-w-0 flex-col self-stretch border-l-0 bg-[#ffffff] [grid-column:2]",
            mob("min-w-[22mm] max-w-[22mm]", "sm:min-w-[22mm] sm:max-w-[22mm]")
          )}>
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden border-b border-black/20 bg-[#f1f5f9]">
              {holderPhotoUrl?.trim() ? (
                <img
                  src={holderPhotoUrl.trim()}
                  alt=""
                  className={cn(
                    "h-full min-h-0 w-full",
                    id1Layout ? "object-contain object-center bg-[#f1f5f9]" : "object-cover object-[center_top]"
                  )}
                />
              ) : (
                <div className="flex h-full min-h-0 w-full flex-1 items-center justify-center p-[0.3mm] text-center text-[1.4mm] font-semibold text-[#94a3b8]">
                  {translateUi("Photo")}
                </div>
              )}
            </div>
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center bg-[#ffffff] p-[0.35mm] [-webkit-print-color-adjust:exact] [print-color-adjust:exact]">
              <QRCodeSVG
                value={verifyUrl.trim()}
                size={112}
                level="Q"
                includeMargin={true}
                className="h-auto w-full max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        ) : (
          <div className={cn(
            "flex min-h-0 w-full min-w-0 flex-col self-stretch border-l-0 bg-[#ffffff] [grid-column:2]",
            mob("min-w-[22mm] max-w-[22mm]", "sm:min-w-[22mm] sm:max-w-[22mm]")
          )}>
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden border-b border-black/20 bg-[#f1f5f9]">
              {holderPhotoUrl?.trim() ? (
                <img
                  src={holderPhotoUrl.trim()}
                  alt=""
                  className={cn(
                    "h-full min-h-0 w-full",
                    id1Layout ? "object-contain object-center bg-[#f1f5f9]" : "object-cover object-[center_top]"
                  )}
                />
              ) : (
                <div className="flex h-full min-h-0 w-full flex-1 items-center justify-center p-[0.3mm] text-center text-[1.4mm] font-semibold text-[#94a3b8]">
                  {translateUi("Photo")}
                </div>
              )}
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center bg-[#ffffff] p-[0.6mm] text-center">
              <span className="text-[1.55mm] font-semibold tracking-wide text-[#64748b]">{translateUi("QR code shown when pass is issued.")}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const back = (
    <div className="cab-pass-force-light cab-pass-flip-face cab-pass-flip-face-back flex h-full w-full flex-col overflow-hidden rounded-[0.8mm] border-[0.35mm] border-black/25 bg-[#ffffff] p-[1.6mm] text-black shadow-sm [color-scheme:light]">
      <div className="min-h-0 flex-1 overflow-hidden">
        <ConditionsBlock companyName={companyName} companyAddress={companyAddress} />
      </div>
      <div className="mt-[1.2mm] shrink-0 space-y-[0.55mm] border-t border-[#e2e8f0] pt-[1.15mm]">
        <p className="text-[1.75mm] font-semibold text-[#475569]">
          {translateUi("ID NO:")} <span className="font-bold text-black">{safeEmployeeId.trim() || "—"}</span>
        </p>
        <div
          className={cn(
            "rounded-[0.5mm] border px-[1mm] py-[0.5mm] text-center text-[1.75mm] font-bold",
            dateRangeOk ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-amber-600 bg-amber-50 text-amber-950"
          )}
        >
          {dateRangeOk
            ? "Digital check: within validity dates"
            : passExpired
              ? translateUi("Digital check: pass expired — outside printed validity")
              : translateUi("Digital check: outside printed validity dates")}
        </div>
      </div>
    </div>
  );

  const scene = (
    <div
      className="relative inline-block"
      style={{ width: CAB_PASS_CARD_WIDTH_MM, height: CAB_PASS_CARD_HEIGHT_MM }}
    >
      <div
        className="cab-pass-flip-scene [perspective:1200px]"
        style={{ width: CAB_PASS_CARD_WIDTH_MM, height: CAB_PASS_CARD_HEIGHT_MM }}
      >
        <div
          className="cab-pass-flip-inner relative h-full w-full transition-transform duration-700 [-webkit-transform-style:preserve-3d]"
          style={{
            transformStyle: "preserve-3d",
            WebkitTransformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            className="absolute inset-0 h-full w-full overflow-hidden rounded-[0.8mm] [backface-visibility:hidden] [-webkit-backface-visibility:hidden] [-webkit-transform-style:preserve-3d]"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
              transform: `rotateY(0deg) translateZ(${FLIP_FACE_Z})`,
            }}
          >
            {front}
          </div>
          <div
            className="absolute inset-0 h-full w-full overflow-hidden rounded-[0.8mm] [backface-visibility:hidden] [-webkit-backface-visibility:hidden] [-webkit-transform-style:preserve-3d]"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
              transform: `rotateY(180deg) translateZ(${FLIP_FACE_Z})`,
            }}
          >
            {back}
          </div>
        </div>
      </div>
      {passExpired ? <CabPassExpiredOverlay /> : null}
    </div>
  );

  if (!interactive) {
    return (
      <div
        className="cab-pass-static-front relative inline-block"
        style={{ width: CAB_PASS_CARD_WIDTH_MM, height: CAB_PASS_CARD_HEIGHT_MM }}
      >
        {front}
        {passExpired ? <CabPassExpiredOverlay /> : null}
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .cab-pass-flip-trigger:focus-visible {
          outline: 2px solid #6366f1;
          outline-offset: 3px;
          border-radius: 6px;
        }
      `}</style>
      <button
        type="button"
        id={`cab-pass-flip-trigger-${uid}`}
        className="cab-pass-flip-trigger block w-full max-w-full min-w-0 cursor-pointer border-0 bg-transparent p-0 text-left"
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-expanded={flipped}
        aria-controls={`cab-pass-flip-live-${uid}`}
        aria-label={flipped ? translateUi("Show front of cab pass") : translateUi("Show reverse of cab pass (conditions)")}
      >
        <span id={`cab-pass-flip-live-${uid}`} className="sr-only">
          {flipped ? translateUi("Showing reverse: conditions of use.") : translateUi("Showing front of driving cab pass.")}
        </span>
        {scene}
      </button>
    </div>
  );
}
